import { useEffect, useState, useCallback, useRef } from 'react'
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { TopBar } from './TopBar'
import { BottomNav } from './BottomNav'
import { NavBarProvider, useNavBar } from './NavBarContext'
import { Sidebar } from './Sidebar'
import { PageTransition } from './PageTransition'
import { Toaster } from '@/components/ui/sonner'
import { useAuth } from '@/lib/auth/context'
import { supabase, waitForSupabaseReady } from '@/lib/supabase'
import { CelebrationAnimationProvider, useCelebrationAnimationState, useCelebrationAnimationActions } from '@/components/animations/CelebrationAnimationContext'
import { StreakExplosion } from '@/components/animations/StreakExplosion'
import { GemParticles } from '@/components/animations/GemParticles'
import { FloatingWallet } from '@/components/animations/FloatingWallet'
import { initNativeFeatures, isNativePlatform, getPushPermissionStatus } from '@/lib/native'
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation'
import { useTimezoneDetection } from '@/lib/hooks/use-timezone-detection'
import { TimezoneChangeDrawer } from '@/components/notifications/TimezoneChangeDrawer'
import { NotificationPromptDrawer, isNotificationPromptDismissed } from '@/components/notifications/NotificationPromptDrawer'
import { EditHabitDrawer } from '@/components/habits/EditHabitDrawer'
import type { UserProfile, Inventory, EvidenceLog } from '@/types/database'
import { calculateGlobalStreakWithGrace } from '@/lib/streak'

export function AuthenticatedLayout() {
  return (
    <NavBarProvider>
      <CelebrationAnimationProvider>
        <AuthenticatedLayoutContent />
      </CelebrationAnimationProvider>
    </NavBarProvider>
  )
}

function AuthenticatedLayoutContent() {
  const { user, session } = useAuth()
  const { navBarHidden } = useNavBar()
  const navigate = useNavigate()
  const nativeInitializedRef = useRef(false)
  const initialFetchStarted = useRef(false)
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [inventory, setInventory] = useState<Inventory[]>([])
  const [globalStreak, setGlobalStreak] = useState(0)
  const [yearlyLogs, setYearlyLogs] = useState<EvidenceLog[]>([])
  const [restDays, setRestDays] = useState<Set<string>>(new Set())
  const [shieldDays, setShieldDays] = useState<Set<string>>(new Set())
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isTopBarLoading, setIsTopBarLoading] = useState(true)
  
  // Edit Habit Drawer — driven by ?editHabit=<id> in the URL
  const [searchParams, setSearchParams] = useSearchParams()
  const editHabitId = searchParams.get('editHabit')
  const handleEditDrawerChange = useCallback((open: boolean) => {
    if (!open && editHabitId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('editHabit')
        return next
      }, { replace: true })
    }
  }, [editHabitId, setSearchParams])
  
  // Get celebration animation state for StreakExplosion, GemParticles, and FloatingWallet
  const { streakExplosion, gemAnimation, optimisticStreakDelta, floatingWallet, isTopBarVisible } = useCelebrationAnimationState()
  const { onFloatingWalletComplete, clearOptimisticStreak } = useCelebrationAnimationActions()
  
  // Detect timezone changes (e.g., user traveling to a new timezone)
  const { timezoneChange, isUpdating: isTzUpdating, approveChange, dismissChange, neverShowAgain } = useTimezoneDetection(profile, user?.id)
  
  // Enable iOS-style swipe-from-edge to go back
  // Disabled on main tabs since they're root-level
  const location = useLocation()
  const isMainTab = ['/', '/challenges', '/community', '/settings'].includes(location.pathname)
  
  useSwipeNavigation({
    enableBack: !isMainTab,
    threshold: 50,   // 50px swipe to trigger (lower = more responsive)
    edgeWidth: 30,   // iOS standard: 20-30px from edge
    fullScreen: false, // Only from edge, like native iOS
    parallax: false,   // Let PageTransition handle the animation
  })
  
  // Initialize native features (only once, with navigation)
  useEffect(() => {
    if (nativeInitializedRef.current) return
    nativeInitializedRef.current = true
    
    if (isNativePlatform()) {
      initNativeFeatures(navigate).catch((error) => {
        console.error('Failed to initialize native features:', error)
      })
    }
  }, [navigate])
  
  // Show a notification permission prompt after habit creation.
  // Listens for a custom event dispatched by HabitWizard when a habit is successfully created.
  // Only shows if the user hasn't already granted permission or permanently dismissed the prompt.
  useEffect(() => {
    const handleHabitCreated = async () => {
      // Skip if user already dismissed permanently
      if (isNotificationPromptDismissed()) return

      // Check current permission status
      if (isNativePlatform()) {
        const status = await getPushPermissionStatus()
        if (status === 'granted') return // already enabled
      } else if ('Notification' in window) {
        if (Notification.permission === 'granted') return
        if (Notification.permission === 'denied') return // can't re-prompt
      } else {
        return // notifications not supported
      }

      // Small delay so the wizard fully closes before the drawer appears
      setTimeout(() => setShowNotificationPrompt(true), 400)
    }

    window.addEventListener('miac:habit-created', handleHabitCreated)
    return () => window.removeEventListener('miac:habit-created', handleHabitCreated)
  }, [])
  
  // Extract Google avatar URL from session
  useEffect(() => {
    if (session?.user?.user_metadata?.avatar_url) {
      setAvatarUrl(session.user.user_metadata.avatar_url)
    } else if (session?.user?.user_metadata?.picture) {
      setAvatarUrl(session.user.user_metadata.picture)
    }
  }, [session])
  
  // Calculate global streak with "Don't Miss Twice" grace rule
  // A single missed day is a "grace day" that doesn't break the streak.
  // Two consecutive missed days breaks it. Rest days and shield days count as covered.
  const calculateGlobalStreak = useCallback((
    logs: EvidenceLog[],
    restDayDates: Set<string> = new Set(),
    shieldDayDates: Set<string> = new Set()
  ): number => {
    const result = calculateGlobalStreakWithGrace(logs, restDayDates, shieldDayDates)
    return result.streak
  }, [])
  
  // Fetch profile data function (reusable)
  const fetchProfileData = useCallback(async (isInitialLoad = false) => {
    if (!user) return
    
    try {
      // Wait for Supabase to be fully ready before making queries
      await waitForSupabaseReady()
      
      // Fetch profile
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      
      if (profileData) {
        setProfile(profileData as UserProfile)
      }
      
      // Fetch inventory
      const { data: inventoryData } = await supabase
        .from('inventory')
        .select('*')
        .eq('user_id', user.id)
      
      if (inventoryData) {
        setInventory(inventoryData as Inventory[])
      }
      
      // Fetch yearly logs for streak modal and global streak calculation
      const oneYearAgo = new Date()
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
      
      const [logsResult, restDaysResult, shieldDaysResult] = await Promise.all([
        supabase
          .from('evidence_logs')
          .select('*')
          .eq('user_id', user.id)
          .gte('created_at', oneYearAgo.toISOString())
          .order('created_at', { ascending: true }),
        supabase
          .from('rest_days')
          .select('rest_date')
          .eq('user_id', user.id),
        supabase
          .from('shield_usage')
          .select('protected_date')
          .eq('user_id', user.id),
      ])
      
      // Process rest days and shield days
      const restDayDates = new Set<string>(
        (restDaysResult.data || []).map((rd: { rest_date: string }) => rd.rest_date)
      )
      const shieldDayDates = new Set<string>(
        (shieldDaysResult.data || []).map((sd: { protected_date: string }) => sd.protected_date)
      )
      setRestDays(restDayDates)
      setShieldDays(shieldDayDates)
      
      if (logsResult.data) {
        const logs = logsResult.data as EvidenceLog[]
        setYearlyLogs(logs)
        // Calculate global streak with "Don't Miss Twice" grace rule
        // Rest days and shield days count as covered days (not gaps)
        const calculatedStreak = calculateGlobalStreak(logs, restDayDates, shieldDayDates)
        setGlobalStreak(calculatedStreak)
        // Clear optimistic delta since real data now reflects the correct streak
        // This prevents the brief double-count when optimistic update + real update overlap
        clearOptimisticStreak()
      }
      
      // Mark loading as complete after initial fetch
      if (isInitialLoad) {
        setIsTopBarLoading(false)
      }
    } catch (error) {
      console.error('Error fetching profile data:', error)
      // Still set loading to false to avoid infinite skeleton
      if (isInitialLoad) {
        setIsTopBarLoading(false)
      }
    }
  }, [user, calculateGlobalStreak, clearOptimisticStreak])
  
  // Fetch user profile, inventory, and yearly logs
  useEffect(() => {
    if (!user) return
    
    // Prevent double fetch from StrictMode
    if (initialFetchStarted.current) return
    initialFetchStarted.current = true
    
    fetchProfileData(true) // Pass true for initial load
    
    // Subscribe to profile changes for real-time updates
    const profileChannel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          setProfile(payload.new as UserProfile)
          setGlobalStreak((payload.new as UserProfile).current_streak_global || 0)
          // Clear optimistic delta since real data now reflects the correct streak
          clearOptimisticStreak()
        }
      )
      .subscribe()
    
    // Subscribe to inventory changes for real-time updates
    const inventoryChannel = supabase
      .channel('inventory-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'inventory',
          filter: `user_id=eq.${user.id}`
        },
        async () => {
          // Refetch inventory when any change happens
          const { data: inventoryData } = await supabase
            .from('inventory')
            .select('*')
            .eq('user_id', user.id)
          
          if (inventoryData) {
            setInventory(inventoryData as Inventory[])
          }
        }
      )
      .subscribe()
    
    // Also listen for evidence_logs changes to refetch profile (for shards updates)
    const logsChannel = supabase
      .channel('evidence-logs-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'evidence_logs',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Refetch profile when new evidence log is created (habit completed)
          fetchProfileData()
        }
      )
      .subscribe()
    
    // Listen for custom event to refresh profile (fallback for when real-time doesn't work)
    const handleProfileRefresh = () => {
      fetchProfileData()
    }
    window.addEventListener('miac:profile-refresh', handleProfileRefresh)
    
    return () => {
      supabase.removeChannel(profileChannel)
      supabase.removeChannel(inventoryChannel)
      supabase.removeChannel(logsChannel)
      window.removeEventListener('miac:profile-refresh', handleProfileRefresh)
    }
  }, [user, fetchProfileData, clearOptimisticStreak])
  
  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])
  
  
  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <Sidebar />
      
      {/* Offline Indicator Banner */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-50 bg-amber-500 dark:bg-amber-600 text-white px-4 py-2 text-center text-sm font-medium shadow-lg"
            style={{ paddingTop: 'var(--native-safe-area-inset-top, env(safe-area-inset-top, 0px))' }}
          >
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span>You&apos;re offline - Some features may be limited</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Top Bar with stats (mobile only) - hidden when e.g. pomodoro timer is active */}
      {!navBarHidden && (
        <TopBar
          profile={profile}
          inventory={inventory}
          globalStreak={globalStreak + optimisticStreakDelta}
          yearlyLogs={yearlyLogs}
          avatarUrl={avatarUrl}
          isLoading={isTopBarLoading}
        />
      )}
      
      {/* Main Content - natural document flow with padding for fixed bars */}
      {/* Mobile: extra padding for TopBar + BottomNav. Desktop: sidebar margin + TopBar padding */}
      <main 
        className="lg:ml-16 lg:pb-8 pt-[calc(var(--native-safe-area-inset-top,env(safe-area-inset-top,0px))+100px)] pb-[calc(var(--native-safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+120px)]"
      >
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>
      
      {/* Mobile Bottom Navigation - hidden when e.g. pomodoro timer is active */}
      {!navBarHidden && <BottomNav />}
      
      <Toaster position="bottom-center" />
      
      {/* Gem Particles Animation */}
      <GemParticles
        active={gemAnimation.active}
        amount={gemAnimation.amount}
        targetPosition={gemAnimation.targetPosition}
      />
      
      {/* Streak Explosion Animation */}
      <StreakExplosion
        active={streakExplosion.active}
        targetPosition={streakExplosion.targetPosition}
      />
      
      {/* Floating Wallet - shows when scrolled down and earning gems */}
      <FloatingWallet
        active={floatingWallet.active}
        startValue={floatingWallet.startValue}
        endValue={floatingWallet.endValue}
        onComplete={onFloatingWalletComplete}
        isTopBarVisible={isTopBarVisible}
      />
      
      {/* Notification permission prompt (shown after habit creation) */}
      <NotificationPromptDrawer
        open={showNotificationPrompt}
        onOpenChange={setShowNotificationPrompt}
      />
      
      {/* Timezone change detection drawer (travel detection) */}
      <TimezoneChangeDrawer
        change={timezoneChange}
        isUpdating={isTzUpdating}
        onApprove={approveChange}
        onDismiss={dismissChange}
        onNeverShowAgain={neverShowAgain}
      />
      
      {/* Edit Habit Drawer — accessible from any page via ?editHabit=<id> */}
      <EditHabitDrawer
        open={!!editHabitId}
        onOpenChange={handleEditDrawerChange}
        habitId={editHabitId}
      />
    </div>
  )
}
